import { Checkbox } from '@/global/components/Checkbox'
import { cn } from '@/lib/utils'
import type { FC, ReactNode } from 'react'
import {
    Controller,
    type UseControllerProps,
    useFormContext,
} from 'react-hook-form'

type FormFieldCheckboxProps = Omit<UseControllerProps, 'control'> & {
    label: ReactNode
    /** Conteúdo à direita do rótulo — normalmente um `IconeAjuda`. */
    endAdornment?: ReactNode
    defaultChecked?: boolean | 'S' | 'N' | null
    disabled?: boolean
    className?: string
    /**
     * Roda antes de o formulário receber o novo valor. Recebe o estado
     * **depois** do clique.
     */
    onChange?: (marcado: boolean) => void
}

/**
 * `Checkbox` ligado ao react-hook-form. Aceita `'S'`/`'N'` vindos da API além
 * de booleano; o valor guardado no formulário é sempre booleano.
 *
 * Ocupa `h-14`, a altura de um `FormField` `medium`, para alinhar com os demais
 * campos quando os dois dividem a mesma linha da grade.
 */
export const FormFieldCheckbox: FC<FormFieldCheckboxProps> = ({
    name,
    rules,
    label,
    endAdornment,
    defaultChecked = false,
    onChange: onChangeMiddleware,
    disabled,
    className,
}) => {
    const { control } = useFormContext()
    const desabilitado = disabled || (control as any)._options?.disabled

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={getValueChecked(defaultChecked)}
            render={({ field: { value, onChange, ref, ...field } }) => (
                <Checkbox
                    {...field}
                    disabled={desabilitado}
                    checked={getValueChecked(value)}
                    onCheckedChange={estado => {
                        const marcado = estado === true
                        onChangeMiddleware?.(marcado)
                        onChange(marcado)
                    }}
                    label={label}
                    endAdornment={endAdornment}
                    containerClassName={cn('h-14 select-none', className)}
                />
            )}
        />
    )
}

function getValueChecked(value: boolean | string | null): boolean | undefined {
    if (value === null) {
        return
    }

    if (typeof value === 'boolean') {
        return value
    }

    if (typeof value === 'string') {
        return value === 'S' ? true : false
    }

    return false
}
